import { useEffect, useState } from 'react'
import { posterUrl } from '../lib/tmdb'
import { paletteFromPoster, cachedPalette } from '../lib/nowPlaying'
import { ReelMark, REEL_LABELS } from './ReelRating'
import { runLine } from '../lib/keepsake'

// ── KeepsakeCard: the Final Cut card, drawn in CSS ────────────────────────────
// A 2:3 card the show's own colour: poster under a scrim that fades to the
// poster-derived ground, a 35mm sprocket edge top and bottom (the same detail
// the Wrap theme gives the Home deck — the one card 17 is built around gets it
// here for real), a slate number top-left, and the member's block at the foot:
// show name in serif, the rating in its own colour, the run in mono, the take
// in italic. Everything scales off `width` so the shelf thumbnail and the sheet's
// hero are the same drawing.
export function KeepsakeCard({ k, width = 280, style }) {
  // Palette from the device cache on the first frame (no flash from the fallback
  // ground to the show's colour when a swipe hands one card off to the next),
  // derived asynchronously only when this poster has never been seen here.
  const [pal, setPal] = useState(() => cachedPalette(k?.poster_path))
  useEffect(() => {
    const hit = cachedPalette(k?.poster_path)
    if (hit) { setPal(hit); return }
    let alive = true
    if (k?.poster_path) paletteFromPoster(k.poster_path).then(p => { if (alive) setPal(p) }).catch(() => {})
    return () => { alive = false }
  }, [k?.poster_path])
  if (!k) return null
  const s = width / 280
  const bg = pal?.bg || '#0b0b12', raised = pal?.raised || '#14141f', vivid = pal?.vivid || pal?.accent || '#f5c842'
  const rk = k.rating ? Math.round(k.rating * 2) / 2 : 0
  const rl = rk ? REEL_LABELS[rk] : null
  const run = runLine(k)
  const poster = k.poster_path ? posterUrl(k.poster_path, width > 200 ? 'w500' : 'w342') : null
  const year = k.completed_at ? new Date(k.completed_at).getFullYear() : null

  return (
    <div className="nx-keepsake" style={{
      position: 'relative', width, aspectRatio: '2 / 3', borderRadius: 18 * s, overflow: 'hidden', flexShrink: 0,
      background: raised, border: `1px solid color-mix(in srgb, ${vivid} 45%, transparent)`,
      boxShadow: `0 ${18 * s}px ${44 * s}px rgba(0,0,0,0.5), 0 0 0 1px rgba(var(--photo-ink-rgb),0.04) inset`,
      fontFamily: 'var(--font)', color: '#fff', ...style,
    }}>
      {poster && <img src={poster} alt="" draggable="false" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: `linear-gradient(to bottom, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 22%, color-mix(in srgb, ${bg} 55%, transparent) 52%, ${bg} 74%)` }} />
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: `radial-gradient(90% 40% at 50% 100%, color-mix(in srgb, ${vivid} 22%, transparent), transparent 70%)`, mixBlendMode: 'screen', opacity: 0.8 }} />

      {/* Slate number */}
      <div style={{ position: 'absolute', top: 20 * s, left: 14 * s, display: 'inline-flex', alignItems: 'center', gap: 6 * s, padding: `${4 * s}px ${9 * s}px`, borderRadius: 999, background: 'rgba(4,4,6,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', border: '1px solid rgba(var(--photo-ink-rgb),0.14)', fontFamily: 'var(--font-mono)', fontSize: 9.5 * s, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(var(--photo-ink-rgb),0.88)', whiteSpace: 'nowrap' }}>
        Final Cut{k.no ? <span style={{ color: vivid }}>No. {k.no}</span> : null}
      </div>

      {/* The member's block */}
      <div style={{ position: 'absolute', left: 18 * s, right: 18 * s, bottom: 24 * s, display: 'flex', flexDirection: 'column', gap: 6 * s }}>
        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24 * s, fontWeight: 500, lineHeight: 1.08, letterSpacing: '-0.01em', textWrap: 'balance', textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}>{k.show_name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 * s, minHeight: 18 * s }}>
          {rl ? (<>
            <ReelMark value={rk} size={16 * s} />
            <span style={{ fontSize: 12.5 * s, fontWeight: 600, color: rl.color }}>{rl.label}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5 * s, color: 'rgba(var(--photo-ink-rgb),0.6)' }}>{rk}/5</span>
          </>) : (
            <span style={{ fontSize: 12 * s, color: 'rgba(var(--photo-ink-rgb),0.5)' }}>Unrated</span>
          )}
        </div>
        {(run || k.episodes || year) && (
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10 * s, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(var(--photo-ink-rgb),0.62)', lineHeight: 1.5 }}>
            {[k.episodes ? `${k.episodes} ep` : null, run, year].filter(Boolean).join(' · ')}
          </div>
        )}
        {/* Season stamps: one tick per season closed, the way a passport shows where
            it's been. Only when there's more than one — a miniseries doesn't need a
            row of one. Not a tier (every finished show's card has all of its seasons
            stamped); it's the shape of the run, readable across a shelf. */}
        {k.seasons > 1 && (
          <div aria-label={`${k.seasons} seasons`} style={{ display: 'flex', alignItems: 'center', gap: 3 * s, marginTop: 2 * s }}>
            {Array.from({ length: Math.min(k.seasons, 10) }, (_, i) => (
              <span key={i} style={{ width: Math.max(8, 14 * s), height: 4 * s, borderRadius: 2 * s, background: `color-mix(in srgb, ${vivid} 85%, #fff)`, opacity: 0.9, boxShadow: `0 0 ${6 * s}px color-mix(in srgb, ${vivid} 50%, transparent)` }} />
            ))}
            {/* A South Park (29 seasons) shouldn't wear three rows of ticks: ten, then the count. */}
            {k.seasons > 10 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9 * s, color: 'rgba(var(--photo-ink-rgb),0.7)', marginLeft: 2 * s }}>×{k.seasons}</span>}
          </div>
        )}
        {k.take && (
          <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 13 * s, lineHeight: 1.35, color: 'rgba(var(--photo-ink-rgb),0.86)', marginTop: 2 * s, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            “{k.take}”
          </div>
        )}
      </div>
    </div>
  )
}
